import { Paper, Button, Container, Grid, Typography } from '@material-ui/core';
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import SwitchInput from '../components/SwitchInput';
import DescriptionInput from '../components/DescriptionInput.jsx';
import { isAndroid } from '../util/helperFunctions';
import ApiMessage from '../components/ApiMessage.jsx';
import { updateEntry, clearEntryMessage } from '../redux/entry';
import MoneyInput from '../components/MoneyInput.jsx';
import DateInput from '../components/DateInput.jsx';
import { useSelector, useDispatch } from 'react-redux';
import TaskInput from '../components/TaskInput.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import ReceiptDisplay from '../components/ReceiptDisplay.jsx';
import { AttachFile, CameraAltOutlined } from '@material-ui/icons';
import { openUploadReceipt, openCaptureReceipt } from '../redux/receipt';

const useStyles = makeStyles({
  buttonContainer: {
    textAlign: 'center',
  },
});
const ExpenseForm = ({
  onCancel,
  onSave,
  disabled,
  closeLabel = 'Close',
  cancelLabel = 'Cancel',
  defaultDirty = false,
  apiLabel,
}) => {
  const classes = useStyles();
  const [errors, setErrors] = useState({});
  const entry = useSelector((state) => state.entry);
  const hasReceipt = useSelector((state) => state.receipt.receipt?.file ? true : false);
  const markDeleted = useSelector((state) => state.receipt.markDeleted);
  const [dirty, setDirty] = useState(defaultDirty);
  const timecardEndDate = useSelector((state) => state.timecard?.endDate);
  const [confirm, setConfirm] = useState(false);
  const dispatch = useDispatch();

  //return if there's no entry
  if (!entry?.id) return '';

  useEffect(() => {
    if (hasReceipt || markDeleted) {
      setDirty(true);
    }
  }, [hasReceipt, markDeleted]);

  const updateEntryData = (updates) => {
    setDirty(true);
    dispatch(updateEntry(updates));
  };

  const onSaveButton = () => {
    dispatch(clearEntryMessage());
    if (
      !entry.incidentId ||
      !entry.taskId ||
      !entry.startDate ||
      !entry.expenseAmount ||
      !entry.description ||
      entry.startDate.substr(0, 10) > timecardEndDate
    ) {
      setErrors({
        incident: !entry.incidentId ? 'Required' : undefined,
        task: !entry.taskId ? 'Required' : undefined,
        startDate: !entry.startDate
          ? 'Required'
          : entry.startDate.substr(0, 10) > timecardEndDate
            ? 'After period end'
            : undefined,
        expenseAmount: !entry.expenseAmount ? 'Required' : undefined,
        description: !entry.description ? 'Required' : undefined,
      });
      return false;
    }
    onSave({
      ...entry,
      endDate: entry.startDate,
      endTime: '1900-01-01T00:00:00.000Z',
      startTime: '1900-01-01T00:00:00.000Z',
      status: 'STOPPED',
    });
  };
  const onCancelButton = () => {
    if (dirty) setConfirm(true);
    else if (onCancel) onCancel({ ...entry, status: 'STOPPED' });
  };

  return (
    <Container component={Paper} style={{ paddingBottom: 2, overflowX: 'hidden' }}>
      <ConfirmDialog
        open={confirm}
        title="Are you sure you want to cancel?"
        message="All changes will be lost"
        onYes={() => {
          if (onCancel) {
            onCancel({ ...entry, status: 'STOPPED' });
          } else {
            updateEntryData({ status: 'STOPPED' });
          }
        }}
        onNo={() => {
          setConfirm(false);
        }}
      />
      <form className={classes.root} noValidate autoComplete="off">
        <TaskInput
          entry={entry}// TODO IK consider passing only entries that are needed
          onChange={(t) => {
            if (t) {
              updateEntryData({ ...t });
            } else {
              updateEntryData({
                taskId: null,
                taskNumber: null,
                taskName: null,
              });
            }
          }}
          disabled={disabled}
          error={errors['incident'] ? true : false}
          helperText={errors['incident']}
        />
        <Grid container>
          <Grid item xs={12} style={{ minHeight: '60px', display: 'flex', alignItems: 'center', gap: '20px' }}>
            {isAndroid() && <CameraAltOutlined onClick={() => dispatch(openCaptureReceipt())} fontSize="default" style={{ cursor: 'pointer' }} titleAccess='Capture receipt' />}
            <AttachFile onClick={() => dispatch(openUploadReceipt())} fontSize="default" style={{ cursor: 'pointer' }} titleAccess='Attach receipt' />
            <ReceiptDisplay />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={6}>
            <DateInput
              value={entry.startDate}
              id="startDate"
              label="Date"
              onChange={(d) => {
                if (d) {
                  updateEntryData({
                    startDate: d,
                  });
                }
              }}
              disabled={disabled}
              error={errors['startDate'] ? true : false}
              helperText={errors['startDate']}
            />
          </Grid>
          <Grid item xs={6}>
            <MoneyInput
              margin="dense"
              label="Amount"
              value={entry.expenseAmount || 0.0}
              setValue={(value) => updateEntryData({ expenseAmount: value })}
              disabled={disabled}
              error={errors['expenseAmount'] ? true : false}
              helperText={errors['expenseAmount']}
              setDirty={(value) => setDirty(value)}
            />
          </Grid>
          <Grid item xs={12}>
            <DescriptionInput
              id="description"
              value={entry.description}
              onChange={(val) => updateEntryData({ description: val })}
              disabled={disabled}
              error={errors['description'] ? true : false}
              helperText={errors['description']}
              setDirty={(value) => setDirty(value)}
            />
          </Grid>
          <Grid item xs={6}>
            <SwitchInput
              checked={entry.noCharge == 1}
              onChange={(e) => {
                updateEntryData({ noCharge: e.target.checked ? 1 : 0 });
              }}
              name="no-charge"
              color="primary"
              label="No charge"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={6}>
            <SwitchInput
              checked={entry.payBonus == 1}
              name="pay-bonus"
              color="primary"
              onChange={(e) => {
                updateEntryData({ payBonus: e.target.checked ? 1 : 0 });
              }}
              label="Reimburse"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container style={{ justifyContent: 'flex-end' }}>
              <Grid item xs={2}>
                <ApiMessage action={apiLabel} />
              </Grid>
              <Grid item className={classes.buttonContainer}>
                {!disabled && dirty && (
                  <Button onClick={onSaveButton} variant="contained" color="primary">
                    <Typography>Save</Typography>
                  </Button>
                )}
              </Grid>
              <Grid item style={{ paddingLeft: 8 }} className={classes.buttonContainer}>
                <Button onClick={onCancelButton} variant="contained">
                  <Typography>
                    {dirty && cancelLabel}
                    {!dirty && closeLabel}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ textAlign: 'center' }}>
            <Typography style={{ paddingTop: 4, color: 'red' }}>
              {entry.validationMessage}
            </Typography>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default ExpenseForm;
